import { useMutation } from '@apollo/client';
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import {
  getLeadOwnerId,
  isLeadOwnedBy,
  isLeadUnassigned,
} from 'lib/sales-agent-leads';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';

import { isManagerOrOwner, isSalesAgent } from 'utils/roles';

import { claimLead, releaseLead } from 'queries/leads';

import { useOrganization } from 'providers/organization';
import { useSession } from 'providers/session';

type Props = {
  leadId: string;
  who?: string | null;
  claimedAt?: string | null;
  ownerName?: string | null;
  onChanged?: () => void;
};

function memberName(member: {
  user?: { name?: string | null; surname?: string | null } | null;
}) {
  return [member.user?.name, member.user?.surname].filter(Boolean).join(' ');
}

const MENU_WIDTH = 224;

function AssignListbox({
  ownerId,
  userId,
  assignees,
  busy,
  assigning,
  onSelect,
}: {
  ownerId: string | null;
  userId: string;
  assignees: Array<{ id: string; label: string }>;
  busy: boolean;
  assigning: boolean;
  onSelect: (id: string) => void;
}) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0 });

  return (
    <Listbox
      value={ownerId || ''}
      onChange={onSelect}
      disabled={busy || assignees.length === 0}
    >
      {({ open }) => (
        <AssignListboxMenu
          open={open}
          buttonRef={buttonRef}
          menuStyle={menuStyle}
          setMenuStyle={setMenuStyle}
          ownerId={ownerId}
          userId={userId}
          assignees={assignees}
          assigning={assigning}
        />
      )}
    </Listbox>
  );
}

function AssignListboxMenu({
  open,
  buttonRef,
  menuStyle,
  setMenuStyle,
  ownerId,
  userId,
  assignees,
  assigning,
}: {
  open: boolean;
  buttonRef: RefObject<HTMLDivElement>;
  menuStyle: { top: number; left: number };
  setMenuStyle: (next: { top: number; left: number }) => void;
  ownerId: string | null;
  userId: string;
  assignees: Array<{ id: string; label: string }>;
  assigning: boolean;
}) {
  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8);
      setMenuStyle({
        top: rect.bottom + 4,
        left: Math.max(8, left),
      });
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, buttonRef, setMenuStyle]);

  return (
    <>
      <div ref={buttonRef} className="inline-flex">
        <Listbox.Button className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50">
          {assigning ? 'Assigning…' : 'Assign'}
          <ChevronUpDownIcon className="h-3.5 w-3.5 text-gray-400" />
        </Listbox.Button>
      </div>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <Listbox.Options
              static
              className="fixed z-[80] max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              style={menuStyle}
            >
              {assignees.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">
                  No active team members
                </div>
              ) : (
                assignees.map((member) => (
                  <Listbox.Option
                    key={member.id}
                    value={member.id}
                    className={({ active }) =>
                      `cursor-pointer select-none px-3 py-1.5 ${
                        active
                          ? 'bg-teal-600 text-white'
                          : member.id === ownerId
                            ? 'bg-teal-50 text-teal-800'
                            : 'text-gray-900'
                      }`
                    }
                  >
                    {member.label}
                    {member.id === userId ? (
                      <span className="ml-1 opacity-70">(you)</span>
                    ) : null}
                  </Listbox.Option>
                ))
              )}
            </Listbox.Options>,
            document.body,
          )
        : null}
    </>
  );
}

export default function LeadOwnershipControls({
  leadId,
  who,
  claimedAt,
  ownerName,
  onChanged,
}: Props) {
  const { claims } = useSession();
  const { team } = useOrganization();
  const userId = claims['x-hasura-user-id'] ?? '';
  const manager = isManagerOrOwner(claims);
  const agent = isSalesAgent(claims);
  const [claim, { loading: claiming }] = useMutation(claimLead);
  const [release, { loading: releasing }] = useMutation(releaseLead);

  const lead = { who, claimed_at: claimedAt };
  const unassigned = isLeadUnassigned(lead);
  const mine = isLeadOwnedBy(lead, userId);
  const ownerId = getLeadOwnerId(lead);
  const busy = claiming || releasing;

  const assignees = useMemo(
    () =>
      (team || [])
        .filter((member) => member.active !== false && member.user?.id)
        .map((member) => ({
          id: member.user.id,
          label: memberName(member) || member.user.email || 'Team member',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [team],
  );

  if (!manager && !agent) {
    return null;
  }

  const doClaim = async (assigneeId: string) => {
    if (!assigneeId || assigneeId === ownerId) {
      return;
    }
    await claim({
      variables: {
        id: leadId,
        who: assigneeId,
        claimedAt: new Date().toISOString(),
      },
    });
    onChanged?.();
  };

  const doRelease = async () => {
    await release({ variables: { id: leadId } });
    onChanged?.();
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
      <span className="text-gray-600">
        {unassigned
          ? 'Unassigned (claimable)'
          : mine
            ? 'Assigned to you'
            : `Assigned to ${ownerName || 'another team member'}`}
      </span>
      {unassigned && (agent || manager) ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => doClaim(userId)}
          className="rounded-md bg-purple-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50"
        >
          {claiming ? 'Claiming…' : 'Claim lead'}
        </button>
      ) : null}
      {manager ? (
        <AssignListbox
          ownerId={ownerId}
          userId={userId}
          assignees={assignees}
          busy={busy}
          assigning={claiming}
          onSelect={doClaim}
        />
      ) : null}
      {(mine || manager) && ownerId ? (
        <button
          type="button"
          disabled={busy}
          onClick={doRelease}
          className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          {releasing
            ? 'Releasing…'
            : manager && !mine
              ? 'Release to pool'
              : 'Release'}
        </button>
      ) : null}
    </div>
  );
}
